import { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism-async-light';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('markup', markup);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('sql', sql);
import { Copy, Check } from 'lucide-react';
import { breakpoints } from '@/layouts/breakpoints';

// ─── Code Block ──────────────────────────────────────────────────────────────

const CodeWrapper = styled.div`
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  overflow: hidden;
  margin: 32px 0;
  background: #f8fafc;
`;

const CodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  background: #f0f2f4;
  border-bottom: 1px solid #e1e4e8;
`;

const LanguagePill = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #6e7681;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const CopyBtn = styled.button<{ $copied: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: ${p => (p.$copied ? '#16a34a' : '#6e7681')};
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 6px;
  transition: background 0.12s, color 0.12s;
  font-family: inherit;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: ${p => (p.$copied ? '#16a34a' : '#1a1a1a')};
  }
`;

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [code]);

  return (
    <CopyBtn $copied={copied} onClick={handleCopy}>
      {copied ? <Check size={12} strokeWidth={2.5} /> : <Copy size={12} />}
      {copied ? 'Copied!' : 'Copy'}
    </CopyBtn>
  );
}

// ─── Image ────────────────────────────────────────────────────────────────────

const ImageFigure = styled.figure`
  margin: 40px 0;
`;

const ImageShell = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.05),
    0 4px 24px rgba(0, 0, 0, 0.07);

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const FigCaption = styled.figcaption`
  margin-top: 11px;
  text-align: center;
  font-size: 13px;
  color: #999;
  font-style: italic;
  letter-spacing: 0.01em;
`;

// ─── Prose Wrapper ────────────────────────────────────────────────────────────

const Prose = styled.div`
  font-size: 17px;
  line-height: 1.78;
  color: #1c1c1e;

  p {
    margin: 0 0 22px;
  }

  /* Headings */
  h2 {
    font-size: 23px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
    color: #0a0a0a;
    margin: 52px 0 14px;
    padding-top: 0;

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    font-size: 18px;
    font-weight: 650;
    letter-spacing: -0.01em;
    line-height: 1.4;
    color: #0a0a0a;
    margin: 36px 0 10px;
  }

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #0a0a0a;
    margin: 28px 0 8px;
  }

  /* Lists */
  ul,
  ol {
    margin: 0 0 22px;
    padding-left: 22px;
  }

  li {
    margin-bottom: 7px;
  }

  li::marker {
    color: #b0b0b0;
  }

  /* Inline code */
  :not(pre) > code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.855em;
    background: #f0f2f4;
    border: 1px solid #e1e4e8;
    border-radius: 5px;
    padding: 2px 6px;
    color: #c7254e;
    word-break: break-word;
  }

  /* Blockquote */
  blockquote {
    margin: 36px 0;
    padding: 2px 20px;
    border-left: 3px solid #0a0a0a;

    p {
      font-size: 18px;
      font-style: italic;
      color: #444;
      margin: 0;
    }
  }

  /* Horizontal rule */
  hr {
    border: none;
    border-top: 1px solid #e8e8e8;
    margin: 52px 0;
  }

  /* Links */
  a {
    color: inherit;
    text-decoration-line: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    text-decoration-color: #ccc;
    transition: text-decoration-color 0.15s;

    &:hover {
      text-decoration-color: #0a0a0a;
    }
  }

  /* Bold */
  strong {
    font-weight: 650;
    color: #0a0a0a;
  }

  em {
    font-style: italic;
    color: #444;
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 32px 0;
    font-size: 14.5px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
  }

  th {
    text-align: left;
    font-weight: 600;
    font-size: 11.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #888;
    padding: 11px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid #f2f2f2;
    color: #333;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: #fafafa;
  }

  @media (max-width: ${breakpoints.medium}) {
    font-size: 16px;
    line-height: 1.72;

    h2 {
      font-size: 20px;
    }

    h3 {
      font-size: 17px;
    }

    blockquote p {
      font-size: 17px;
    }
  }
`;

// ─── Custom Components ────────────────────────────────────────────────────────

const components: any = {
  // Strip react-markdown's outer <pre> so it doesn't double-wrap CodeWrapper
  pre({ children }: any) {
    return <>{children}</>;
  },

  code({ node, className, children, ...props }: any) {
    const codeStr = String(children);

    // Inline code never contains a newline.
    // react-markdown always appends \n to fenced block content, so this is reliable.
    if (!codeStr.includes('\n')) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }

    // Block code - fenced with ```, with or without a language specifier
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : null;
    const code = codeStr.replace(/\n$/, '');

    return (
      <CodeWrapper>
        <CodeHeader>
          {language ? <LanguagePill>{language}</LanguagePill> : <span />}
          <CopyButton code={code} />
        </CodeHeader>
        <SyntaxHighlighter
          language={language ?? 'python'}
          style={oneLight}
          customStyle={{
            margin: 0,
            padding: '20px 24px',
            background: 'transparent',
            borderRadius: 0,
            fontSize: '13.5px',
            lineHeight: '1.66',
          }}
          // Keep default PreTag="pre" so Prose's :not(pre) > code rule
          // doesn't bleed inline-pill styles into the highlighter's tokens
          {...props}
        >
          {code}
        </SyntaxHighlighter>
      </CodeWrapper>
    );
  },

  // Paragraphs containing only an image would otherwise nest the block-level
  // <figure>/<div> from the img override inside a <p>, which is invalid HTML.
  p({ node, children }: any) {
    const only = node?.children?.length === 1 ? node.children[0] : null;
    if (only && only.type === 'element' && only.tagName === 'img') {
      return <>{children}</>;
    }
    return <p>{children}</p>;
  },

  img({ src, alt }: any) {
    // Don't show generic alt text as captions
    const showCaption =
      alt &&
      alt.toLowerCase() !== 'image' &&
      alt.toLowerCase() !== 'captionless image' &&
      !alt.toLowerCase().startsWith('image ');

    return (
      <ImageFigure>
        <ImageShell>
          <img src={src} alt={alt ?? ''} loading="lazy" />
        </ImageShell>
        {showCaption && <FigCaption>{alt}</FigCaption>}
      </ImageFigure>
    );
  },
};

// ─── Export ───────────────────────────────────────────────────────────────────

interface Props {
  content: string;
}

export default function MarkdownRenderer({ content }: Props) {
  return (
    <Prose>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Prose>
  );
}
